import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';

import { Icon, InfoIconTooltip } from '@components';
import { TRANSACTIONS } from '@constants/routes';
import { DashboardCardType } from '@features/dashboard/helper';
import { transactionsTabs } from '@features/transactions/constants';
import { updateTransactionFilter } from '@layouts/main-header/branchSlice';
import formatCurrency from '@utils/numberFormater';

// import theme from '../../../../../themes/colors';

interface DashboardCardProps {
  id: DashboardCardType;
  title: string;
  value: number;
  icon: string;
  growthValue?: number;
  className?: string;
  tooltipValue?: string;
  isLoading?: boolean;
  showGrowthValue: boolean;
  isClickable?: boolean;
}

const DashboardCard = ({
  id,
  title,
  value,
  icon,
  growthValue,
  className,
  tooltipValue,
  isLoading,
  showGrowthValue,
  isClickable = true
}: DashboardCardProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const iconName = growthValue && growthValue < 0 ? 'decreasing' : 'increasing';

  const handleClick = () => {
    if (!isClickable) return;
    const filter =
      id === DashboardCardType.COLLECTED ||
      id === DashboardCardType.TO_BE_SETTLED
        ? transactionsTabs[1]
        : transactionsTabs[2];

    dispatch(updateTransactionFilter({ filter }));
    navigate(TRANSACTIONS.TRANSACTIONS);
  };

  const changeInPercentage = () =>
    isLoading ? (
      <Skeleton
        height={32}
        width={80}
        key={Math.random()}
        animation="wave"
        variant="text"
      />
    ) : (
      <div
        className={`flex items-center rounded-xl px-3 py-1 text-sm ${
          growthValue && growthValue < 0
            ? 'bg-errorLite text-error'
            : 'bg-primaryLite9 text-primary90'
        }`}
      >
        <Icon
          name={iconName}
          size="medium"
          className="mr-1 !w-3 min-w-4"
          key={iconName}
        />
        <span>{growthValue && Math.abs(growthValue)}%</span>
      </div>
    );

  return (
    <div
      className={`rounded-xl bg-white p-4 ${className} ${
        !isClickable ? '' : 'cursor-pointer'
      }`}
      onClick={handleClick}
      role="presentation"
    >
      <div className="mb-4 flex items-center gap-3">
        {icon && <Icon name={icon} size="none" className="size-8" />}
        <p className="text-lg font-medium text-content">{title}</p>
        <div>
          {tooltipValue && (
            <InfoIconTooltip
              id={title.replace(/\s+/g, '-')}
              tooltipText={tooltipValue}
              classname="max-w-400"
            />
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center text-3xl leading-8 text-content ">
          {isLoading ? (
            <Skeleton
              height={32}
              width={80}
              key={Math.random()}
              animation="wave"
              variant="text"
            />
          ) : (
            <>
              <span className="font-inter font-medium mb-0.25">₹</span>
              <h2 className="pl-1 font-bold">
                {formatCurrency(+value, 'INR')}
              </h2>
            </>
          )}
        </div>
        {showGrowthValue && changeInPercentage()}
      </div>
    </div>
  );
};

export default DashboardCard;
